import { Pagination } from "antd";
import { apiV2Root } from "consts";
import type { Person } from "features/people/data/types";
import PersonBubble from "features/people/elements/PersonBubble";
import { useDataQuery } from "hooks/useData";
import { useDataQueryParams } from "hooks/useDataQueryParams";
import { useUser } from "hooks/useUser";
import React, { useState } from "react";
import { PaginatedResponse } from "types/basic";
import TabBar, { TabBarOption } from "ui/TabBar";

import "./PeopleList.css";

type ViewMode = "all" | "moderation";
type SortField = "name" | "engagement" | "authority" | "learning" | "rating";

// StandardPagination.max_page_size на бэкенде — 100, больше запросить нельзя
const PAGE_SIZE = 60;

const PeopleList: React.FC = () => {
  const { isModerator } = useUser();

  const [mode, setMode] = useState<ViewMode>("all");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDesc, setSortDesc] = useState(false);
  const [page, setPage] = useState(1);

  const ordering = `${sortDesc ? "-" : ""}${sortField}`;

  const generalResponse = useDataQueryParams<PaginatedResponse<Person>>(
    "people",
    { page, pageSize: PAGE_SIZE, ordering },
    apiV2Root,
    mode !== "all",
  );

  // Очередь на модерации остаётся только для модераторов — рейтинг открыли всем,
  // это не трогает.
  const moderatedPeople = useDataQuery<Person[]>(
    "moderated-people/",
    mode !== "moderation" || !isModerator,
  );

  // Компоненты рейтинга и сортировка по ним — только модераторам. Страница целиком
  // и так доступна только модераторам (см. AppRoutes.tsx), это дополнительный слой.
  const modeOptions: TabBarOption<ViewMode>[] = [
    { value: "all", label: "Все" },
    ...(isModerator
      ? [{ value: "moderation" as const, label: "На модерации" }]
      : []),
  ];

  const sortOptions: TabBarOption<SortField>[] = [
    { value: "name", label: "Имя" },
    ...(isModerator
      ? [
          { value: "engagement" as const, label: "E" },
          { value: "authority" as const, label: "A" },
          { value: "learning" as const, label: "L" },
          { value: "rating" as const, label: "Рейтинг" },
        ]
      : []),
  ];

  const effectiveMode = mode === "moderation" && !isModerator ? "all" : mode;

  const handleModeChange = (nextMode: ViewMode) => {
    setMode(nextMode);
    setPage(1);
  };

  const handleSortFieldChange = (field: SortField) => {
    setSortField(field);
    setPage(1);
  };

  return (
    <div className="people-list">
      <h2 className="people-list__title">Люди</h2>

      <div className="people-list__controls">
        <TabBar
          value={effectiveMode}
          options={modeOptions}
          onChange={handleModeChange}
        />

        {effectiveMode === "all" && (
          <div className="people-list__sort">
            <TabBar
              value={sortField}
              options={sortOptions}
              onChange={handleSortFieldChange}
            />
            <button
              className="people-list__sort-direction"
              onClick={() => {
                setSortDesc((prev) => !prev);
                setPage(1);
              }}
              title={sortDesc ? "По убыванию" : "По возрастанию"}
            >
              {sortDesc ? "↓" : "↑"}
            </button>
          </div>
        )}
      </div>

      {effectiveMode === "all" ? (
        <>
          {!generalResponse ? (
            <div>Загрузка...</div>
          ) : generalResponse.results.length === 0 ? (
            <div>Никого не найдено.</div>
          ) : (
            <div className="people-list__grid">
              {generalResponse.results.map((person) => (
                <PersonBubble
                  key={person.id}
                  person={person}
                />
              ))}
            </div>
          )}

          {generalResponse && generalResponse.totalPages > 1 && (
            <div className="people-list__pagination">
              <Pagination
                current={generalResponse.page}
                pageSize={generalResponse.pageSize}
                total={generalResponse.count}
                onChange={setPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      ) : !moderatedPeople || moderatedPeople.length === 0 ? (
        <div>Никого нет на модерации.</div>
      ) : (
        <ul className="people-list__list">
          {moderatedPeople.map((person) => (
            <li
              key={person.id}
              className="people-list__item"
            >
              <PersonBubble
                person={person}
                moderationMode
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PeopleList;
